/**
 * Meta (Facebook) Pixel Integration
 * Waits for sales channel fbq, configures Advanced Matching,
 * and tracks events with Conversions API support.
 *
 * Standalone version for co-aurora.
 * Ported from shopper/src/scripts/lib/integrations/meta.js
 */
(function() {
  'use strict';

  function Meta(config) {
    this.pixelId = config.pixelId;
    this.currentUserData = {};
    window.TrackingBase.call(this, Object.assign({
      name: 'Meta',
      enableConversionsAPI: true,
      enableDeduplication: true
    }, config));
  }

  Meta.prototype = Object.create(window.TrackingBase.prototype);
  Meta.prototype.constructor = Meta;

  /* ---- Initialize ---- */

  Meta.prototype.initialize = function() {
    this._checkForFbqPeriodically();
  };

  Meta.prototype._checkForFbqPeriodically = function() {
    var self = this;
    var attempts = 0;
    var maxAttempts = 70;

    var interval = setInterval(function() {
      attempts++;
      if (window.fbq && self._isPixelAlreadyInitialized()) {
        self.log('Sales channel Meta Pixel detected after ' + (attempts * 100) + 'ms');
        self._completeFbqSetup();
        clearInterval(interval);
        return;
      }
      if (attempts >= maxAttempts) {
        self.warn('Sales channel Meta Pixel not found after 7s');
        clearInterval(interval);
      }
    }, 100);
  };

  Meta.prototype._isPixelAlreadyInitialized = function() {
    try {
      if (window.fbq && (window.fbq.loaded === true || window.fbq.version ||
        (window.fbq._fbq && window.fbq._fbq.loaded) || (window._fbq && window._fbq.loaded))) {
        return true;
      }
      if (window.fbq && typeof window.fbq.getState === 'function') {
        var state = window.fbq.getState();
        if (state && state.pixels && state.pixels.length > 0) return true;
      }
      if (window._fbq && window._fbq.pixelsByID && Object.keys(window._fbq.pixelsByID).length > 0) return true;
      return false;
    } catch (e) { return true; }
  };

  Meta.prototype._completeFbqSetup = function() {
    this.tagLoaded = true;
    if (!this._isPixelAlreadyInitialized()) return;
    this._configureAdvancedMatching();
  };

  /* ---- Advanced Matching ---- */

  Meta.prototype._configureAdvancedMatching = function() {
    try {
      var amd = {};
      amd.client_user_agent = navigator.userAgent;

      /* IP from attribution */
      if (window.AttributionManager) {
        var sess = window.AttributionManager.getSessionData();
        if (sess && sess.client_ip) amd.client_ip_address = sess.client_ip;
      }

      /* Customer data */
      var customer = window.theme && window.theme.customer;
      if (customer && customer.id) {
        if (customer.email) amd.em = customer.email;
        if (customer.phone) {
          var ph = customer.phone;
          if (ph.length === 10 && !ph.startsWith('1')) ph = '1' + ph;
          amd.ph = ph;
        }
        if (customer.first_name) amd.fn = customer.first_name;
        if (customer.last_name) amd.ln = customer.last_name;
        if (customer.city) amd.ct = customer.city;
        if (customer.state) amd.st = customer.state;
        if (customer.postal_code) amd.zp = customer.postal_code;
        if (customer.country) amd.country = customer.country;
        if (customer.id) amd.external_id = customer.id.toString();
      }

      /* Attribution cookies */
      if (window.AttributionManager) {
        window.AttributionManager.collectCookieData();
        var metaData = window.AttributionManager.getMetaData();
        if (metaData.fbp && metaData.fbp.trim()) amd.fbp = metaData.fbp;
        if (metaData.fbc && metaData.fbc.trim()) {
          amd.fbc = metaData.fbc;
        } else if (metaData.fbclid && metaData.fbclid.trim()) {
          amd.fbc = 'fb.1.' + Date.now() + '.' + metaData.fbclid;
        }

        if (!amd.em && metaData.email) amd.em = metaData.email.toLowerCase().trim();
        if (!amd.ph && metaData.phone) {
          var p = metaData.phone.replace(/\D/g, '');
          if (p.length === 10 && !p.startsWith('1')) p = '1' + p;
          amd.ph = p;
        }
        if (!amd.fn && metaData.first_name) amd.fn = metaData.first_name.toLowerCase().trim();
        if (!amd.ln && metaData.last_name) amd.ln = metaData.last_name.toLowerCase().trim();
        if (!amd.ct && metaData.city) amd.ct = metaData.city.toLowerCase().trim();
        if (!amd.st && metaData.state) amd.st = metaData.state.toLowerCase().trim();
        if (!amd.zp && metaData.zip) amd.zp = metaData.zip.replace(/\s/g, '').toLowerCase();
        if (!amd.country && metaData.country) amd.country = metaData.country.toLowerCase();
      }

      /* Clean empty values */
      var cleaned = {};
      Object.keys(amd).forEach(function(key) {
        if (amd[key] && amd[key].toString().trim()) cleaned[key] = amd[key];
      });

      if (Object.keys(cleaned).length > 0 && window.fbq) {
        window.fbq('init', this.pixelId, cleaned);
        this.currentUserData = cleaned;
        this.log('Advanced Matching configured', { fields: Object.keys(cleaned) });
      }
    } catch (err) {
      this.warn('Failed to configure Advanced Matching', err);
    }
  };

  /* ---- Track ---- */

  Meta.prototype.track = function(eventName, eventData, options) {
    options = options || {};

    /* Event name mapping */
    var nameMap = {
      'cart_viewed': 'ViewCart',
      'product_removed_from_cart': 'RemoveFromCart',
      'product_added_to_cart': 'AddToCart',
      'checkout_initiated': 'InitiateCheckout',
      'checkout_completed': 'Purchase',
      'product_viewed': 'ViewContent',
      'search_submitted': 'Search',
      'page_viewed': 'PageView',
      'payment_info_submitted': 'AddPaymentInfo',
      'contact_info_submitted': 'Lead',
      'form_submitted': 'Lead'
    };
    var metaEventName = nameMap[eventName] || eventName;

    if (!this.tagLoaded && window.fbq) this._completeFbqSetup();
    if (!window.fbq) throw new Error('Meta Pixel fbq not available');

    this._configureAdvancedMatching();

    var metaEventData = this._formatEventDataForMeta(eventData);
    var metaOptions = {};
    if (options.eventID) metaOptions.eventID = options.eventID;

    window.fbq('track', metaEventName, metaEventData, metaOptions);
    this.log('Tracked ' + metaEventName + (eventName !== metaEventName ? ' (from ' + eventName + ')' : ''), { eventId: options.eventID });
  };

  Meta.prototype._formatEventDataForMeta = function(eventData) {
    var f = {};
    if (eventData.value !== undefined) f.value = eventData.value;
    if (eventData.currency) f.currency = eventData.currency;
    if (eventData.content_type) f.content_type = eventData.content_type;
    if (eventData.content_category) f.content_category = eventData.content_category;
    if (eventData.content_name) f.content_name = eventData.content_name;
    if (eventData.num_items) f.num_items = eventData.num_items;

    if (eventData.content_ids) {
      f.content_ids = Array.isArray(eventData.content_ids) ? eventData.content_ids : [eventData.content_ids];
    }
    if (eventData.contents && Array.isArray(eventData.contents)) {
      f.contents = eventData.contents.map(function(item) {
        return { id: item.id || item.item_id, quantity: item.quantity, item_price: item.price || item.item_price };
      });
    }

    if (eventData.search_string) f.search_string = eventData.search_string;

    /* Customer data conversion */
    if (eventData.email) f.em = eventData.email.toLowerCase().trim();
    if (eventData.phone) {
      var ph = eventData.phone.replace(/\D/g, '');
      if (ph.length === 10 && !ph.startsWith('1')) ph = '1' + ph;
      f.ph = ph;
    }
    if (eventData.first_name) f.fn = eventData.first_name.toLowerCase().trim();
    if (eventData.last_name) f.ln = eventData.last_name.toLowerCase().trim();
    if (eventData.city) f.ct = eventData.city.toLowerCase().trim();
    if (eventData.state) f.st = eventData.state.toLowerCase().trim();
    if (eventData.postal_code) f.zp = eventData.postal_code.replace(/\s/g, '').toLowerCase();
    if (eventData.country) f.country = eventData.country.toLowerCase();
    if (eventData.customer_id) f.external_id = eventData.customer_id.toString();

    return f;
  };

  /* Export */
  window.TrackingMeta = Meta;
})();
